import React from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Message } from 'primereact/message';
import { SmartHeader, SmartField, NoRecordsCard } from './_SmartFieldRenderer';

const Section = ({ icon, title, children }) => (
    <div className="pt-3 pb-3 border-bottom-1 surface-border">
        <div className="flex align-items-center gap-2 mb-3">
            <i className={`pi ${icon} text-primary`} aria-hidden="true" />
            <span className="text-sm font-semibold text-900 uppercase">{title}</span>
        </div>
        <div className="grid">{children}</div>
    </div>
);

// `onDownloadPDF`/`pdfPasswordHint` come from SimpleReportForm, which does the
// authenticated blob download. Shared-link and lead-history views render this
// component without them and simply get no button.
const VoterID2OutputComponent = ({ reportData, serviceInfo, hideCredits, isSharedView, onDownloadPDF, pdfPasswordHint: pdfPasswordHintProp }) => {
    const shouldHideCredits = hideCredits || isSharedView;

    let parsedReportData = reportData;
    if (typeof reportData?.report_json === 'string') {
        try {
            parsedReportData = { ...reportData, report_json: JSON.parse(reportData.report_json) };
        } catch {
            // ignore
        }
    }

    const pdfPasswordHint =
        pdfPasswordHintProp ||
        reportData?.pdf_password_hint ||
        parsedReportData?.pdf_password_hint ||
        reportData?.data?.pdf_password_hint ||
        reportData?.response?.pdf_password_hint ||
        reportData?.report_json?.pdf_password_hint ||
        parsedReportData?.report_json?.pdf_password_hint ||
        'Last 4 characters of your Voter ID + "VOTER" (Example: If Voter ID is ABC1234567, password is 4567VOTER)';

    if (reportData?.code === '1007') {
        return <NoRecordsCard subtitle="There is no data found for the provided details. Please enter valid details." />;
    }

    let parsedData = reportData;
    if (typeof reportData === 'string') {
        try { parsedData = JSON.parse(reportData); } catch { /* ignore */ }
    }
    if (reportData?.report_json && typeof reportData.report_json === 'object') parsedData = reportData.report_json;
    else if (reportData?.api_response) parsedData = reportData.api_response;
    else if (reportData?.data && typeof reportData.data === 'object') parsedData = reportData.data;

    if (parsedData?.report_json && parsedData.report_json !== parsedData) {
        if (typeof parsedData.report_json === 'string') {
            try { parsedData = JSON.parse(parsedData.report_json); } catch { /* ignore */ }
        } else if (typeof parsedData.report_json === 'object') {
            parsedData = parsedData.report_json;
        }
    }

    const isStoredReport = reportData?.report_json || reportData?.api_response || reportData?.data;

    let parsedDataForCheck = reportData;
    if (reportData?.report_json && typeof reportData.report_json === 'object') {
        parsedDataForCheck = reportData.report_json;
    }

    const errorCode =
        reportData?.code ||
        reportData?.data?.data?.code ||
        parsedDataForCheck?.data?.data?.code ||
        reportData?.report_json?.data?.data?.code ||
        parsedData?.raw_response?.data?.data?.code ||
        parsedData?.data?.raw_response?.data?.data?.code;

    const hasValidData = errorCode === '1000' || errorCode === 1000;

    if (!isStoredReport && !hasValidData && errorCode === '1007') {
        return (
            <NoRecordsCard subtitle="No voter registration records found for the provided Voter ID. Please verify and try again." />
        );
    }

    if (
        !isStoredReport &&
        (reportData?.code === 'SERVER_ERROR' ||
            parsedData?.raw_response?.status_code === 500 ||
            (parsedData?.raw_response?.success === false && parsedData?.raw_response?.error))
    ) {
        return (
            <Card className="text-center">
                <i className="pi pi-exclamation-triangle text-5xl text-orange-500 block mb-3" aria-hidden="true" />
                <h3 className="text-lg font-semibold text-900 m-0 mb-2">Server Error</h3>
                <p className="text-600 text-sm m-0">There was a server error from the API provider. Please try again later. No credits were deducted.</p>
            </Card>
        );
    }

    let voterRawData = null;
    if (parsedData?.data?.raw_response?.data?.data?.voter_data) voterRawData = parsedData.data.raw_response.data.data.voter_data;
    else if (parsedData?.raw_response?.data?.data?.voter_data) voterRawData = parsedData.raw_response.data.data.voter_data;
    else if (parsedData?.data?.data?.voter_data) voterRawData = parsedData.data.data.voter_data;
    else if (parsedData?.voter_data) voterRawData = parsedData.voter_data;
    else if (parsedData?.data?.voter_data) voterRawData = parsedData.data.voter_data;
    else if (parsedData?.data?.response) voterRawData = parsedData.data.response;
    else if (parsedData?.response) voterRawData = parsedData.response;
    else if (parsedData?.document_id || parsedData?.name || parsedData?.voter_id) voterRawData = parsedData;
    else voterRawData = parsedData;

    const voterData = voterRawData
        ? {
              voter_id: voterRawData.document_id || voterRawData.voter_id || voterRawData.epic_number || null,
              document_type: voterRawData.document_type || 'VOTER',
              name: voterRawData.name || voterRawData.voter_name || null,
              father_name: voterRawData.father_name || voterRawData.relative_name || null,
              husband_name: voterRawData.husband_name || null,
              gender: voterRawData.gender || voterRawData.sex || null,
              age: voterRawData.age || null,
              dob: voterRawData.dob || voterRawData.date_of_birth || null,
              district: voterRawData.district || null,
              state: voterRawData.state || null,
              assembly_constituency_number: voterRawData.assembly_constituency_number || voterRawData.ac_number || null,
              assembly_constituency_name: voterRawData.assembly_constituency_name || voterRawData.ac_name || null,
              parliamentary_constituency:
                  voterRawData.parliamentary_constituency_name || voterRawData.parliamentary_constituency || voterRawData.pc_name || null,
              parliamentary_constituency_number: voterRawData.parliamentary_constituency_number || null,
              part_number: voterRawData.part_number || voterRawData.part_no || null,
              part_name: voterRawData.part_name || null,
              section_number: voterRawData.section_number || voterRawData.section_no || null,
              serial_number: voterRawData.serial_number || voterRawData.serial_no || null,
              house_number: voterRawData.house_number || voterRawData.house_no || null,
              polling_station: voterRawData.polling_station || null,
              address: voterRawData.address || null,
          }
        : null;

    if (!voterData) {
        return <NoRecordsCard subtitle="The voter data structure is not recognized. Please try again." />;
    }

    const remainingBalance =
        reportData?.remaining_credits || reportData?.data?.remaining_credits || serviceInfo?.remaining_credits;

    return (
        <div>
            <SmartHeader title="Voter ID Verification Report" icon="pi-id-card" remainingBalance={remainingBalance} shouldHideCredits={shouldHideCredits} />

            <Card>
                <Section icon="pi-user" title="Personal Information">
                    <SmartField name="voter_id_epic_number" value={voterData.voter_id} highlight />
                    <div className="col-12 sm:col-6 lg:col-4 flex flex-column gap-1">
                        <span className="text-500 text-xs font-medium">Document Type</span>
                        <span><Tag value={voterData.document_type || 'N/A'} severity="info" /></span>
                    </div>
                    <SmartField name="name" value={voterData.name} />
                    {voterData.father_name && <SmartField name="father_name" value={voterData.father_name} />}
                    {voterData.husband_name && <SmartField name="husband_name" value={voterData.husband_name} />}
                    <SmartField name="gender" value={voterData.gender} />
                    {voterData.age && <SmartField name="age" value={`${voterData.age} years`} />}
                    {voterData.dob && <SmartField name="date_of_birth" value={voterData.dob} />}
                </Section>

                <Section icon="pi-map" title="Electoral Information">
                    <SmartField name="state" value={voterData.state} />
                    <SmartField name="district" value={voterData.district} />
                    <SmartField
                        name="assembly_constituency"
                        value={
                            voterData.assembly_constituency_name
                                ? `${voterData.assembly_constituency_name} (${voterData.assembly_constituency_number || 'N/A'})`
                                : voterData.assembly_constituency_number
                        }
                    />
                    {(voterData.parliamentary_constituency || voterData.parliamentary_constituency_number) && (
                        <SmartField
                            name="parliamentary_constituency"
                            value={
                                voterData.parliamentary_constituency
                                    ? `${voterData.parliamentary_constituency}${
                                          voterData.parliamentary_constituency_number ? ` (${voterData.parliamentary_constituency_number})` : ''
                                      }`
                                    : voterData.parliamentary_constituency_number
                            }
                        />
                    )}
                    {voterData.polling_station && <SmartField name="polling_station" value={voterData.polling_station} />}
                </Section>

                {(voterData.part_number || voterData.part_name || voterData.section_number || voterData.serial_number || voterData.house_number) && (
                    <Section icon="pi-list" title="Electoral Details">
                        {voterData.part_number && <SmartField name="part_number" value={voterData.part_number} />}
                        {voterData.part_name && <SmartField name="part_name" value={voterData.part_name} />}
                        {voterData.section_number && <SmartField name="section_number" value={voterData.section_number} />}
                        {voterData.serial_number && <SmartField name="serial_number" value={voterData.serial_number} />}
                        {voterData.house_number && <SmartField name="house_number" value={voterData.house_number} />}
                    </Section>
                )}

                {voterData.address && (
                    <Section icon="pi-home" title="Registered Address">
                        <div className="col-12 flex flex-column gap-1">
                            <span className="text-500 text-xs font-medium">Full Address</span>
                            <span className="text-900 text-sm font-semibold" style={{ wordBreak: 'break-word' }}>{voterData.address}</span>
                        </div>
                    </Section>
                )}

                {onDownloadPDF && (
                    <div className="text-center pt-3">
                        <Button
                            label="Download Voter ID V2 Report"
                            icon="pi pi-file-pdf"
                            onClick={onDownloadPDF}
                            size="small"
                        />
                        {pdfPasswordHint && (
                            <div className="mt-2">
                                <Message severity="info" text={pdfPasswordHint} />
                            </div>
                        )}
                    </div>
                )}
            </Card>
        </div>
    );
};

export default VoterID2OutputComponent;
